import React, { memo, useMemo } from 'react'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterCellProps,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { StandardCommission } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { IntroducingBrokerProfileAccountType } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileAccountType'
import { DropdownFilterCell } from 'components/Filter/FilterCell/DropdownFilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { useGridState } from 'features/DataGrid'
import { useAppSelector } from 'store'
import { TableStateContextData } from 'utils/TableStateContext'

interface StandardCommissionsGridProps {
  standardCommissions: StandardCommission[]
  isEdit: boolean
  onEditStandardCommissions(data: StandardCommission): void
  introducingBrokerProfileAccountType: IntroducingBrokerProfileAccountType[]
  tableData: TableStateContextData
  onHandleGridPageChange(pageNumber: number): void
  onHandleGridSortChange(event: GridSortChangeEvent): void
  onHandleDeleteStandardCommission(data: StandardCommission): void
}

const useMapStateToProps = () => ({
  rebateCalcMethods: useAppSelector((state) =>
    state.partnerships.rebateCalcMethods.map((x) => x.name)
  ),
})

export const StandardCommissionsGrid: React.FC<StandardCommissionsGridProps> = (props) => {
  const { rebateCalcMethods = [] } = useMapStateToProps()
  const { gridState } = useGridState()

  const RebateCalculateMethodFilterCell = useMemo(
    () => createDropdownFilterCell(rebateCalcMethods),
    [rebateCalcMethods]
  )

  const handlePageChange = (event: GridPageChangeEvent) => {
    props.onHandleGridPageChange(event.page.skip)
  }

  const handleSortChange = (event: GridSortChangeEvent) => {
    props.onHandleGridSortChange(event)
  }

  return (
    <Grid
      {...gridState}
      sortable
      pageable
      resizable
      data={props?.standardCommissions}
      skip={gridState.skip}
      take={gridState.take}
      total={gridState.total}
      sort={gridState.sort}
      onPageChange={handlePageChange}
      onSortChange={handleSortChange}
      data-test='table'
    >
      <Column
        field='PlatformTypes'
        title='Platform'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.platformTypes?.map((pt) => pt.name).join(',')}</td>
        }}
      />
      <Column
        field='InstrumentTypes'
        title='Instrument Type'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.instrumentTypes?.map((it) => it.name).join(',')}</td>
        }}
      />
      <Column
        field='Instruments'
        title='Instrument'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.instruments?.map((i) => i.symbol).join(',')}</td>
        }}
      />
      <Column
        field='TradingAccountTypes'
        title='Account Type'
        cell={(gridCellProps: GridCellProps) => {
          const standardCommissions = gridCellProps.dataItem as StandardCommission
          return <td>{standardCommissions?.tradingAccountTypes?.map((tt) => tt.name).join(',')}</td>
        }}
      />
      <Column
        field='RebateCalculationMethod'
        title='Rebate Calc Method'
        filterCell={RebateCalculateMethodFilterCell}
        cell={(gridCellProps: GridCellProps) => {
          const dataItem = gridCellProps.dataItem as StandardCommission
          return <td>{dataItem.rebateCalculationMethod.name}</td>
        }}
      />
      <Column
        field='Amount'
        title='Rebate Amount'
        filter='numeric'
        cell={(gridCellProps: GridCellProps) => {
          const dataItem = gridCellProps.dataItem as StandardCommission
          if (dataItem.rebateCalculationMethod.id === 5) {
            return (
              <td>
                {dataItem.pointValue}, {dataItem.amount}%
              </td>
            )
          }
          return <td>{dataItem.amount}</td>
        }}
      />
      <Column
        filterable={false}
        title=''
        width={50}
        cell={(gridCellProps: GridCellProps) => (
          <td>
            <MoreButton
              disabled={!props.isEdit}
              items={[
                {
                  name: 'Edit',
                  disabled: !props.isEdit,
                  onClick: () => props.onEditStandardCommissions(gridCellProps.dataItem),
                },
                {
                  name: 'Delete',
                  disabled: !props.isEdit,
                  onClick: () => {
                    props.onHandleDeleteStandardCommission(gridCellProps.dataItem)
                  },
                },
              ]}
            />
          </td>
        )}
      />
    </Grid>
  )
}

const createDropdownFilterCell = (data: string[]) => {
  return memo((props: GridFilterCellProps) => {
    return <DropdownFilterCell {...props} data={data} defaultItem='' />
  })
}
